import { Directive, effect, ElementRef, inject, Injectable, input, output } from '@angular/core';
import { ProductMediaHttp } from './media-http.service';

/**
 * What a preview is a preview OF: `/api/vn/adm/product-media/7/preview?kind=frame&v=3` → `7:frame` (the version `v` changes with every new frame, the item and the kind do not).
 * An address that does not look like that has no identity and is kept until the page is left.
 */
export function previewIdentity(url: string): string | null {
  const match = /\/(\d+)\/preview\?(?:[^#]*&)?kind=(\w+)/.exec(url);
  return match ? `${match[1]}:${match[2]}` : null;
}

/**
 * The private previews of the job (`previewUrl`, `stripUrl`) need the login of adm, and an `<img>` cannot send it: they are fetched with `HttpClient` as blobs and
 * shown from object URLs. The address of an item carries its version (`&v=`), so a new frame is a new address and a new fetch; the picture of the version it replaces is
 * given back to the memory at once (one version of an item and kind is kept), and `release` gives the rest back when the page is left.
 */
@Injectable()
export class MediaPreviewCache {
  private readonly api = inject(ProductMediaHttp);
  private readonly cache = new Map<string, Promise<string>>();
  /** The address that is the current version of each item and kind. */
  private readonly current = new Map<string, string>();

  /** The object URL of a preview; a failed fetch is forgotten, so the next ask tries again. */
  get(url: string): Promise<string> {
    let found = this.cache.get(url);
    if (!found) {
      this.supersede(url);
      found = this.api.preview(url).then(blob => URL.createObjectURL(blob));
      found.catch(() => this.cache.delete(url));
      this.cache.set(url, found);
    }
    return found;
  }

  /** Revokes every object URL: the page is gone. */
  release(): void {
    for (const pending of this.cache.values()) {
      pending.then(objectUrl => URL.revokeObjectURL(objectUrl), () => undefined);
    }
    this.cache.clear();
    this.current.clear();
  }

  /** A new version of an item's preview is asked for: the picture of the one before it is not needed any more. */
  private supersede(url: string): void {
    const identity = previewIdentity(url);
    if (identity === null) {
      return;
    }
    const old = this.current.get(identity);
    this.current.set(identity, url);
    const pending = old !== undefined && old !== url ? this.cache.get(old) : undefined;
    if (old !== undefined && pending) {
      // forgotten as well as revoked: should anything ask for that version again it is fetched anew, never handed a dead address
      this.cache.delete(old);
      pending.then(objectUrl => URL.revokeObjectURL(objectUrl), () => undefined);
    }
  }
}

/** `<img [hcMediaPic]="previewUrl">`: draws a private preview. While it is on its way the image has no `src`; a preview that cannot be had is `failed`. */
@Directive({ selector: 'img[hcMediaPic]' })
export class MediaPicDirective {
  readonly url = input<string | null>(null, { alias: 'hcMediaPic' });
  readonly failed = output<void>();

  private readonly cache = inject(MediaPreviewCache);
  private readonly host = inject<ElementRef<HTMLImageElement>>(ElementRef);
  private current: string | null = null;

  constructor() {
    effect(() => {
      const url = this.url();
      this.current = url;
      const img = this.host.nativeElement;
      if (!url) {
        img.removeAttribute('src');
        return;
      }
      img.removeAttribute('src');
      this.cache.get(url).then(
        objectUrl => {
          if (this.current === url) {
            img.src = objectUrl;
          }
        },
        () => {
          if (this.current === url) {
            this.failed.emit();
          }
        }
      );
    });
  }
}
